import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { LoginFormPanel } from '@/views/login/LoginFormPanel';
import { LoginShowcasePanel } from '@/views/login/LoginShowcasePanel';

/**
 * Split-screen login: animated showcase (desktop) + auth form against coolture_auth.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, loginNotice, clearLoginNotice } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showValidationHints, setShowValidationHints] = useState(false);

  const handleUsernameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      clearLoginNotice();
      setUsername(event.target.value);
    },
    [clearLoginNotice]
  );

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      clearLoginNotice();
      setPassword(event.target.value);
    },
    [clearLoginNotice]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage(null);
      clearLoginNotice();
      setShowValidationHints(true);

      const result = await login(username, password);
      if (result.success) {
        navigate('/home', { replace: true });
        return;
      }

      if (result.error) {
        setErrorMessage(result.error.message);
      }
    },
    [clearLoginNotice, login, navigate, password, username]
  );

  const handleGoogleError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  return (
    <div
      data-testid="agents-login"
      className="agents-login-atf-shell flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-canvas lg:flex-row"
    >
      <div className="hidden h-full min-h-0 w-1/2 shrink-0 lg:flex">
        <LoginShowcasePanel />
      </div>

      <div className="flex h-full min-h-0 w-full flex-col lg:w-1/2">
        <LoginFormPanel
          username={username}
          password={password}
          isLoading={isLoading}
          loginNotice={loginNotice}
          errorMessage={errorMessage}
          showValidationHints={showValidationHints}
          onUsernameChange={handleUsernameChange}
          onPasswordChange={handlePasswordChange}
          onSubmit={handleSubmit}
          onGoogleError={handleGoogleError}
          onClearNotice={clearLoginNotice}
        />
      </div>
    </div>
  );
}
